import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { resolveAvatarUrl, handleAvatarError } from "../utils/profilePicUrl";
import { sanitizeHtml } from "../utils/sanitizeHtml";
import AuthorNameLink from "./shared/AuthorNameLink";
import { getApiBaseUrl } from "../config/api";

/**
 * Dedicated blog reading page. Separate from the article reader.
 */
export default function BlogDetail({ posts }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const [fetched, setFetched] = useState(null);
  const [loading, setLoading] = useState(false);

  const fromList = useMemo(() => {
    const list = Array.isArray(posts) ? posts : [];
    return list.find((p) => String(p.id) === String(id)) || null;
  }, [posts, id]);

  useEffect(() => {
    if (fromList || !id) return undefined;
    let cancelled = false;
    setLoading(true);
    fetch(`${getApiBaseUrl()}/get_posts.php?post_id=${encodeURIComponent(id)}`)
      .then((res) => res.json())
      .then((data) => {
        if (cancelled) return;
        const post = data.success && Array.isArray(data.posts) ? data.posts[0] : null;
        setFetched(post && post.post_type === "blog" ? post : null);
      })
      .catch(() => {
        if (!cancelled) setFetched(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [fromList, id]);

  const post = fromList?.post_type === "blog" ? fromList : fromList ? null : fetched;

  if (loading && !post) {
    return (
      <div style={{ maxWidth: 720, margin: "40px auto", padding: 20, textAlign: "center" }}>
        <p>Loading blog…</p>
      </div>
    );
  }

  if (!post || post.post_type !== "blog") {
    return (
      <div style={{ maxWidth: 720, margin: "40px auto", padding: 20, textAlign: "center" }}>
        <p>Blog not found.</p>
        <button type="button" onClick={() => navigate("/blogs")}>
          Back to blogs
        </button>
      </div>
    );
  }

  const html = sanitizeHtml(post.content || post.text || "");
  const created = post.created_at ? new Date(post.created_at).toLocaleString() : "";

  return (
    <div
      style={{
        maxWidth: 780,
        margin: "0 auto",
        padding: "24px 16px 48px",
        background: "#fff",
        minHeight: "100vh",
      }}
    >
      <button
        type="button"
        onClick={() => navigate(-1)}
        style={{
          border: "none",
          background: "transparent",
          color: "#0f766e",
          cursor: "pointer",
          fontSize: 14,
          marginBottom: 16,
          padding: 0,
        }}
      >
        ← Back
      </button>

      {post.cover_image && (
        <img
          src={post.cover_image}
          alt=""
          style={{
            width: "100%",
            maxHeight: 360,
            objectFit: "cover",
            borderRadius: 12,
            marginBottom: 20,
            display: "block",
          }}
        />
      )}

      <div
        style={{
          display: "inline-block",
          fontSize: 11,
          fontWeight: 600,
          letterSpacing: 0.4,
          textTransform: "uppercase",
          color: "#0f766e",
          background: "#ccfbf1",
          padding: "3px 8px",
          borderRadius: 4,
          marginBottom: 10,
        }}
      >
        Blog
      </div>

      <h1
        style={{
          fontSize: 32,
          fontWeight: 700,
          color: "#0f172a",
          lineHeight: 1.25,
          margin: "0 0 16px",
        }}
      >
        {post.title}
      </h1>

      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 12,
          marginBottom: 28,
          paddingBottom: 16,
          borderBottom: "1px solid #e2e8f0",
        }}
      >
        <img
          src={resolveAvatarUrl(post)}
          alt=""
          onError={handleAvatarError}
          style={{
            width: 48,
            height: 48,
            borderRadius: "50%",
            objectFit: "cover",
          }}
        />
        <div>
          <div style={{ fontWeight: 600 }}>
            <AuthorNameLink user={post} style={{ fontWeight: 600 }} />
          </div>
          <div style={{ fontSize: 13, color: "#64748b" }}>
            {created}
            {post.category ? ` · ${post.category}` : ""}
            {post.country ? ` · ${post.country}` : ""}
          </div>
        </div>
      </div>

      <article
        className="cool-blog-body"
        style={{
          fontSize: 17,
          lineHeight: 1.7,
          color: "#1e293b",
        }}
        dangerouslySetInnerHTML={{ __html: html }}
      />

      <style>{`
        .cool-blog-body h2 { font-size: 1.4em; margin: 1.2em 0 0.5em; color: #0f172a; }
        .cool-blog-body h3 { font-size: 1.2em; margin: 1em 0 0.4em; color: #0f172a; }
        .cool-blog-body p { margin: 0 0 1em; }
        .cool-blog-body ul, .cool-blog-body ol { margin: 0 0 1em; padding-left: 1.4em; }
        .cool-blog-body a { color: #0f766e; }
        .cool-blog-body blockquote {
          border-left: 3px solid #99f6e4;
          margin: 1em 0;
          padding-left: 1em;
          color: #475569;
        }
      `}</style>

      <div style={{ marginTop: 32 }}>
        <Link to={`/home/post/${post.id}`} style={{ fontSize: 14, color: "#64748b" }}>
          View as post with comments →
        </Link>
      </div>
    </div>
  );
}
